import {useEffect} from 'react';
import type {Editor} from 'grapesjs';
import {getSupportedVisibilityChangeEvent} from '@Utils/browser.util';
import {saveEmailTemplateToDb} from '@EmailMakerReact/lib/email-maker-storage-library';

/**
 * Page-lifecycle guards for the email maker:
 *
 * - Unsaved-changes prompt: this hook owns `window.onbeforeunload` exclusively (the GrapesJS config
 *   sets `noticeOnUnload: false`, because GrapesJS never clears its handler on destroy and would
 *   leave a stale prompt after unmount). Armed while the dirty count is non-zero, torn down with the hook.
 * - Navigate-away DB flush: on visibilitychange→hidden and on beforeunload (a backup for browsers
 *   that skip visibilitychange on refresh/close), flush the cached payload to the database.
 *   `saveEmailTemplateToDb` guards internally so an unchanged template doesn't re-hit the endpoint.
 */
export function useEmailMakerLifecycleGuards(editor: Editor | null): void {
  useBeforeUnloadGuard(editor);
  useNavigateAwayDbFlush(editor);
}

function useBeforeUnloadGuard(editor: Editor | null): void {
  useEffect((): (() => void) | undefined => {
    if (!editor) {
      return undefined;
    }
    const handler = (event: BeforeUnloadEvent): void => {
      if (editor.getDirtyCount() === 0) {
        return;
      }
      event.preventDefault();
      // eslint-disable-next-line @typescript-eslint/no-deprecated
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return (): void => {
      window.removeEventListener('beforeunload', handler);
    };
  }, [editor]);
}

function useNavigateAwayDbFlush(editor: Editor | null): void {
  useEffect((): (() => void) | undefined => {
    if (!editor) {
      return undefined;
    }
    const [visibilityChangeEvent, visibilityHiddenProp] = getSupportedVisibilityChangeEvent();
    const flush = (): void => {
      void saveEmailTemplateToDb(editor);
    };
    const onVisibilityChange = (): void => {
      if (isDocumentHidden(visibilityHiddenProp)) {
        flush();
      }
    };
    document.addEventListener(visibilityChangeEvent, onVisibilityChange);
    window.addEventListener('beforeunload', flush);
    return (): void => {
      document.removeEventListener(visibilityChangeEvent, onVisibilityChange);
      window.removeEventListener('beforeunload', flush);
    };
  }, [editor]);
}

/** The hidden-property name is browser-dependent (see getSupportedVisibilityChangeEvent), so the read is dynamic. */
function isDocumentHidden(visibilityHiddenProp: string): boolean {
  return Boolean(Reflect.get(document, visibilityHiddenProp));
}
